'use client';

import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Delete,
  NextKeyframeFill,
  PreviousKeyframeFill,
  Remove,
} from '@crabs-ai/web-icons';
import { ToolButton, ValueInputField } from '@crabs-ai/ds-react';
import { PlayerRefContext } from '../../context-provider';
import {
  DEFAULT_PAGE_TRANSITION_DURATION_IN_SECONDS,
  findNextPageKeyframe,
  findPrevPageKeyframe,
  getActivePageAt,
  hasPageKeyframeAt,
  removePageKeyframeAt,
  upsertPageKeyframe,
  type PageKeyframe,
} from '../../items/pdf/page-keyframes';
import { isDocumentItem } from '../../items/pdf/pdf-item-type';
import { changeItem } from '../../state/actions/change-item';
import { EditorStarterItem } from '../../items/item-type';
import { useFps, useItem, useWriteContext } from '../../utils/use-context';
import { useCurrentPlayerFrame } from '../../utils/use-current-player-frame';

/** position-keyframe-controls 와 같은 에디터 다이아 그래픽. */
const KeyframeDiamond: React.FC<{ filled: boolean; className?: string }> = ({
  filled,
  className,
}) => (
  <svg
    viewBox="0 0 16 16"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M8 1.5 14.5 8 8 14.5 1.5 8Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 헤더 컨트롤 행 — [‹ 이전] [◆/◇ 토글] [› 다음] … [🗑 전체삭제].
 * 플레이어 프레임 구독은 이 소형 컴포넌트에만(재생 중 상위 리렌더 방지).
 */
const PageKeyframeHeaderControls: React.FC<{
  keyframes: PageKeyframe[];
  from: number;
  maxLocalFrame: number;
  onSeekToLocalFrame: (localFrame: number) => void;
  onAddAt: (localFrame: number) => void;
  onRemoveAt: (localFrame: number) => void;
  onRemoveAll: () => void;
}> = ({
  keyframes,
  from,
  maxLocalFrame,
  onSeekToLocalFrame,
  onAddAt,
  onRemoveAt,
  onRemoveAll,
}) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const localFrame = Math.round(globalFrame - from);
  const isInRange = localFrame >= 0 && localFrame <= maxLocalFrame;
  const hasKf = isInRange && hasPageKeyframeAt(keyframes, localFrame);
  const prevKf = findPrevPageKeyframe(keyframes, localFrame);
  const nextKf = findNextPageKeyframe(keyframes, localFrame);

  return (
    <div className="flex items-center gap-4xs">
      <ToolButton
        size="sm"
        state={prevKf ? 'default' : 'disabled'}
        onClick={() => {
          if (prevKf) onSeekToLocalFrame(prevKf.frame);
        }}
        aria-label={t('Editor.inspector.page-keyframes.prev')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.page-keyframes.prev')}
      >
        <PreviousKeyframeFill />
      </ToolButton>

      <ToolButton
        size="sm"
        state={isInRange ? 'default' : 'disabled'}
        onClick={() => {
          if (!isInRange) return;
          if (hasKf) onRemoveAt(localFrame);
          else onAddAt(localFrame);
        }}
        aria-label={
          hasKf
            ? t('Editor.inspector.page-keyframes.toggle-remove')
            : t('Editor.inspector.page-keyframes.toggle-add')
        }
        isShowTooltip
        tooltipNode={
          hasKf
            ? t('Editor.inspector.page-keyframes.toggle-remove')
            : t('Editor.inspector.page-keyframes.toggle-add')
        }
      >
        <KeyframeDiamond
          filled={hasKf}
          className={
            !isInRange
              ? undefined
              : hasKf
                ? 'text-sys-cont-primary-default'
                : 'text-sys-cont-neutral-light'
          }
        />
      </ToolButton>

      <ToolButton
        size="sm"
        state={nextKf ? 'default' : 'disabled'}
        onClick={() => {
          if (nextKf) onSeekToLocalFrame(nextKf.frame);
        }}
        aria-label={t('Editor.inspector.page-keyframes.next')}
        isShowTooltip
        tooltipNode={t('Editor.inspector.page-keyframes.next')}
      >
        <NextKeyframeFill />
      </ToolButton>

      {keyframes.length > 0 && (
        <div className="flex min-w-0 flex-1 justify-end">
          <ToolButton
            size="sm"
            onClick={onRemoveAll}
            aria-label={t('Editor.inspector.page-keyframes.remove-all')}
            isShowTooltip
            tooltipNode={t('Editor.inspector.page-keyframes.remove-all')}
          >
            <Delete />
          </ToolButton>
        </div>
      )}
    </div>
  );
};

/** 키프레임 리스트 행 — 행 클릭 = 그 키프레임으로 시크. 시간(s) + 페이지(1-based). */
const PageKeyframeRowUnmemoized: React.FC<{
  keyframe: PageKeyframe;
  index: number;
  from: number;
  fps: number;
  maxTimeInSeconds: number;
  numPages: number;
  onSeekToLocalFrame: (localFrame: number) => void;
  onEdit: (index: number, patch: Partial<PageKeyframe>) => void;
  onRemove: (index: number) => void;
}> = ({
  keyframe,
  index,
  from,
  fps,
  maxTimeInSeconds,
  numPages,
  onSeekToLocalFrame,
  onEdit,
  onRemove,
}) => {
  const t = useTranslations();
  const globalFrame = useCurrentPlayerFrame();
  const isActive =
    Math.round(globalFrame - from) === Math.round(keyframe.frame);

  const stopSeek = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div
      onClick={() => onSeekToLocalFrame(keyframe.frame)}
      className={`flex cursor-pointer items-center gap-4xs rounded-3xs px-4xs py-5xs transition-colors ${
        isActive ? 'bg-sys-bg-surface-primary' : 'hover:bg-sys-bg-surface-hover'
      }`}
    >
      <KeyframeDiamond
        filled
        className={`size-2 shrink-0 ${
          isActive
            ? 'text-sys-cont-primary-default'
            : 'text-sys-cont-neutral-light'
        }`}
      />

      <div
        className="flex items-center gap-4xs"
        onClick={stopSeek}
      >
        <ValueInputField
          value={Number((keyframe.frame / fps).toFixed(2))}
          setValue={(update) =>
            onEdit(index, { frame: Math.round(update.num * fps) })
          }
          min={0}
          max={Number(maxTimeInSeconds.toFixed(2))}
          step={0.1}
          isUnit="s"
          showStepper={false}
          accessibilityLabel={t('Editor.inspector.page-keyframes.time')}
          className="w-[64px] shrink-0"
        />
        <ValueInputField
          value={keyframe.pageIndex + 1}
          setValue={(update) =>
            onEdit(index, {
              pageIndex: Math.min(
                Math.max(0, Math.round(update.num) - 1),
                numPages - 1,
              ),
            })
          }
          min={1}
          max={numPages}
          step={1}
          isUnit="p"
          showStepper={false}
          accessibilityLabel={t('Editor.inspector.page-keyframes.page')}
          className="w-[64px] shrink-0"
        />
      </div>

      <div className="flex min-w-0 flex-1 justify-end">
        <ToolButton
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
          title={t('Editor.inspector.page-keyframes.remove')}
        >
          <Remove />
        </ToolButton>
      </div>
    </div>
  );
};

const PageKeyframeRow = memo(PageKeyframeRowUnmemoized);

/**
 * 페이지 키프레임 편집 — "이 시점부터 이 페이지" 를 기록하는 페이지 타임라인.
 * 경계마다 종이 넘김(플립) 전환이 걸리며, 전환 길이는 아래 입력으로 조절(0 = 컷).
 *
 * - ◆/◇ 토글: 플레이헤드에 키프레임이 있으면 삭제, 없으면 "현재 유효 페이지 + 1"
 *   (마지막 페이지면 그대로)로 추가 — 넘김 시나리오의 합리적 기본값.
 * - v1 은 단일 선택 전용 — 호출부(PdfInspector)가 게이트한다.
 */
const PageKeyframeControlsUnmemoized: React.FC<{
  itemId: string;
  durationInFrames: number;
  numPages: number;
}> = ({ itemId, durationInFrames, numPages }) => {
  const t = useTranslations();
  const { fps } = useFps();
  const { setState } = useWriteContext();
  const playerRef = useContext(PlayerRefContext);
  const item = useItem(itemId);

  const documentItem = isDocumentItem(item) ? item : null;
  const keyframes = useMemo<PageKeyframe[]>(
    () => documentItem?.pageKeyframes ?? [],
    [documentItem],
  );
  const basePageIndex = documentItem?.pageIndex ?? 0;
  const from = item?.from ?? 0;
  const transitionDurationInSeconds =
    documentItem?.pageTransitionDurationInSeconds ??
    DEFAULT_PAGE_TRANSITION_DURATION_IN_SECONDS;

  const commit = useCallback(
    (next: PageKeyframe[]) => {
      setState({
        update: (state) =>
          changeItem(state, itemId, (i) => {
            if (!isDocumentItem(i)) return i;
            if (next.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { pageKeyframes: _removed, ...rest } = i;
              return rest as EditorStarterItem;
            }
            return { ...i, pageKeyframes: next };
          }),
        commitToUndoStack: true,
      });
    },
    [setState, itemId],
  );

  const seekToLocalFrame = useCallback(
    (localFrame: number) => {
      playerRef?.current?.seekTo(Math.round(from + localFrame));
    },
    [playerRef, from],
  );

  // 추가 — 현재 유효 페이지 + 1 (마지막이면 유지). "다음 장 넘김" 기본값.
  const addAt = useCallback(
    (localFrame: number) => {
      const activePage = getActivePageAt(keyframes, basePageIndex, localFrame);
      const nextPage = Math.min(activePage + 1, Math.max(0, numPages - 1));
      commit(
        upsertPageKeyframe(keyframes, {
          frame: localFrame,
          pageIndex: nextPage,
        }),
      );
    },
    [keyframes, basePageIndex, numPages, commit],
  );

  const removeAt = useCallback(
    (localFrame: number) => {
      commit(removePageKeyframeAt(keyframes, localFrame));
    },
    [keyframes, commit],
  );

  const removeAll = useCallback(() => {
    commit([]);
  }, [commit]);

  const handleEdit = useCallback(
    (index: number, patch: Partial<PageKeyframe>) => {
      const target = keyframes[index];
      if (!target) return;
      const rest = keyframes.filter((_, i) => i !== index);
      commit(upsertPageKeyframe(rest, { ...target, ...patch }));
    },
    [keyframes, commit],
  );

  const handleRemove = useCallback(
    (index: number) => {
      commit(keyframes.filter((_, i) => i !== index));
    },
    [keyframes, commit],
  );

  const handleTransitionDurationChange = useCallback(
    (seconds: number) => {
      setState({
        update: (state) =>
          changeItem(state, itemId, (i) => {
            if (!isDocumentItem(i)) return i;
            const clamped = Math.min(3, Math.max(0, seconds));
            if (i.pageTransitionDurationInSeconds === clamped) return i;
            return { ...i, pageTransitionDurationInSeconds: clamped };
          }),
        commitToUndoStack: true,
      });
    },
    [setState, itemId],
  );

  const maxLocalFrame = Math.max(0, durationInFrames - 1);
  const maxTimeInSeconds = Math.max(0, (durationInFrames - 1) / fps);

  return (
    <div className="flex flex-col gap-2xs">
      <PageKeyframeHeaderControls
        keyframes={keyframes}
        from={from}
        maxLocalFrame={maxLocalFrame}
        onSeekToLocalFrame={seekToLocalFrame}
        onAddAt={addAt}
        onRemoveAt={removeAt}
        onRemoveAll={removeAll}
      />

      {/* 넘김 전환 길이 — 0 은 컷 */}
      <div className="flex items-center justify-between gap-4xs">
        <span className="text-caption-small text-sys-cont-neutral-light">
          {t('Editor.inspector.page-keyframes.transition-duration')}
        </span>
        <ValueInputField
          value={Number(transitionDurationInSeconds.toFixed(1))}
          setValue={(update) => handleTransitionDurationChange(update.num)}
          min={0}
          max={3}
          step={0.1}
          isUnit="s"
          showStepper={false}
          accessibilityLabel={t(
            'Editor.inspector.page-keyframes.transition-duration',
          )}
          className="w-[64px] shrink-0"
        />
      </div>

      {keyframes.length === 0 ? (
        <span className="text-caption-small text-sys-cont-neutral-lightest">
          {t('Editor.inspector.page-keyframes.empty')}
        </span>
      ) : (
        <div className="flex flex-col gap-4xs border-t border-sys-style-neutral-light pt-2xs">
          {keyframes.map((keyframe, index) => (
            <PageKeyframeRow
              key={`${keyframe.frame}`}
              keyframe={keyframe}
              index={index}
              from={from}
              fps={fps}
              maxTimeInSeconds={maxTimeInSeconds}
              numPages={numPages}
              onSeekToLocalFrame={seekToLocalFrame}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const PageKeyframeControls = memo(PageKeyframeControlsUnmemoized);
